// Block layer for the scheduler-composer program editor — model + derivation.
//
// A Block is an AUTHORING-ONLY concept (next/program-editor-redesign.md §2.1):
// "these exercises, with this prescription, on days D and weeks W". Persistence
// is untouched — blocks derive FROM the matrix Draft (programEdit.ts) on edit
// and expand BACK into it on Add/Update (programBlocksExpand.ts). No block
// identity is ever stored.
//
// Derivation must never fail (§2.2): slots the composer can't express
// (multi-variant cadence, non-default phase) come back as VERBATIM blocks that
// carry the SlotDraft untouched, so derive → expand → buildPayload is lossless
// (§3.2 hard requirement). Everything else groups by content: same members,
// same prescription, same week-set across sessions → one composed block.
//
// Keep free of React / UI imports. Companion: programBlocksExpand.ts.

import type { Draft, IntensityKind, SlotDraft } from "./programEdit.js";

// One prescribed set inside a block — uniform across the block's weeks.
// Strings like the Draft cells so the composer inputs bind directly.
export type BlockSetRow = {
  reps: string;
  intensityKind: IntensityKind;
  intensityValue: string; // "" for value-less kinds (none/history)
  note: string; // "" when absent
};

// One exercise of the block (1 member = standalone slot, ≥2 = superset).
export type BlockMember = {
  exerciseId: string;
  exerciseName: string | undefined;
  setRows: BlockSetRow[];
  instructions: string;
  amrapLastSet: boolean;
  maxAttempt: boolean;
  rpeEnabled: boolean;
  rirEnabled: boolean;
};

export type BlockGroup = { id: string; kind: "superset" | "circuit" };

// Where the block lands in one session. slotIds/positions are parallel to
// members[]: the existing slot each member writes back to ("" = create a new
// slot) and its original index in the session (-1 = append). Carrying ids
// keeps PATCH round-trips stable; carrying positions lets expandBlocks
// restore the original exercise order. group preserves the stored grouping
// (id + circuit/superset kind) for that day.
export type BlockPlacement = {
  dayIdx: number;
  slotIds: string[];
  positions: number[];
  group: BlockGroup | null;
};

export type ComposedBlock = {
  kind: "composed";
  members: BlockMember[];
  weeks: number[]; // 0-based, sorted, non-empty
  placements: BlockPlacement[]; // sorted by dayIdx, non-empty
};

// Escape hatch: a slot the composer can't express survives verbatim.
export type VerbatimBlock = {
  kind: "verbatim";
  dayIdx: number;
  position: number;
  slot: SlotDraft;
};

export type Block = ComposedBlock | VerbatimBlock;

export const blockDays = (b: Block): number[] =>
  b.kind === "verbatim" ? [b.dayIdx] : b.placements.map((p) => p.dayIdx);

// Deep-copy a SlotDraft so block operations never alias the source draft.
export const cloneSlotDraft = (slot: SlotDraft): SlotDraft => ({
  ...slot,
  setRows: slot.setRows.map((r) => ({
    ...r,
    cells: r.cells.map((c) => ({ ...c })),
    notesPerCell: [...r.notesPerCell],
  })),
  variantsRaw: slot.variantsRaw.map((v) => ({ ...v })),
  inactiveWeeks: [...slot.inactiveWeeks],
  group: slot.group ? { ...slot.group } : null,
});

// The slot's variants can be reproduced by the single every-week default
// buildPayload synthesises (i.e. the composer loses nothing by rewriting it).
export const hasComposableVariants = (slot: SlotDraft): boolean => {
  if (slot.variantsRaw.length > 1) return false;
  const v0 = slot.variantsRaw[0];
  return !v0 || (v0.cadence === "every-week" && v0.phaseOffset === 0);
};

// A slot runs on at least one week — the "not a phantom" test shared by
// composability, the post-edit sweep, and the %MAX validator. One predicate so
// the four callers can't drift (an all-inactive slot has no sets to save).
export const hasActiveWeek = (slot: SlotDraft): boolean =>
  slot.inactiveWeeks.some((off) => !off);

// A slot is composable when its variants are reproducible AND it has at
// least one active week. Anything else round-trips verbatim.
export const isComposableSlot = (slot: SlotDraft): boolean =>
  hasComposableVariants(slot) && hasActiveWeek(slot);

// The slot's prescription at week w, in block shape. Notes are trimmed to
// match buildPayload's write-time normalisation so signatures don't split on
// whitespace.
const rowsAtWeek = (slot: SlotDraft, w: number): BlockSetRow[] =>
  slot.setRows.map((r) => ({
    reps: r.cells[w]?.reps ?? "0",
    intensityKind: r.intensityKind,
    intensityValue: r.cells[w]?.intensityValue ?? "",
    note: (r.notesPerCell[w] ?? "").trim(),
  }));

const memberAtWeek = (slot: SlotDraft, w: number): BlockMember => ({
  exerciseId: slot.exerciseId,
  exerciseName: slot.exerciseName,
  setRows: rowsAtWeek(slot, w),
  instructions: slot.instructions.trim(),
  amrapLastSet: slot.amrapLastSet,
  maxAttempt: slot.maxAttempt,
  rpeEnabled: slot.rpeEnabled,
  rirEnabled: slot.rirEnabled,
});

// Content signature of a member — exerciseName included defensively so two
// slots whose stored snapshots drifted apart never collapse into one block
// (expanding would overwrite one of the names).
const memberKey = (m: BlockMember): string =>
  JSON.stringify([
    m.exerciseId,
    m.exerciseName ?? "",
    m.instructions,
    m.amrapLastSet,
    m.maxAttempt,
    m.rpeEnabled,
    m.rirEnabled,
    m.setRows.map((r) => [r.reps, r.intensityKind, r.intensityValue, r.note]),
  ]);

type Unit = { slots: SlotDraft[]; positions: number[] };

// Partition a session's slots into group instances (slots sharing a group id,
// in position order) with standalone slots as singletons. Instance order
// follows the first member's position.
const sessionUnits = (slots: SlotDraft[]): Unit[] => {
  const units: Unit[] = [];
  const byGroup = new Map<string, number>();
  slots.forEach((slot, pos) => {
    if (slot.group) {
      const gi = byGroup.get(slot.group.id);
      if (gi != null) {
        units[gi]!.slots.push(slot);
        units[gi]!.positions.push(pos);
        return;
      }
      byGroup.set(slot.group.id, units.length);
    }
    units.push({ slots: [slot], positions: [pos] });
  });
  return units;
};

// Re-derive the block list from a Draft. Total by construction: every active
// (slot, week) cell lands in exactly one composed block, every non-composable
// slot in exactly one verbatim block. The worst case is one block per slot —
// derivation never fails on "dirty" programs (redesign §8.4).
export const deriveBlocks = (draft: Draft): Block[] => {
  const out: Block[] = [];
  // Cross-day coalescing: fragments with identical (members content, week-set)
  // merge into one multi-day block. Same-day duplicates (the same prescription
  // twice in one session) bucket into separate blocks, k-th occurrence each.
  const buckets = new Map<string, ComposedBlock[]>();

  draft.sessions.forEach((session, dayIdx) => {
    for (const inst of sessionUnits(session.slots)) {
      // A group only stays a multi-member unit when every member is
      // composable AND they share the same active-week set; otherwise each
      // slot stands alone (its grouping survives via placement.group).
      const activity = (s: SlotDraft) =>
        s.inactiveWeeks.map((off) => (off ? "0" : "1")).join("");
      const splitApart =
        inst.slots.some((s) => !isComposableSlot(s)) ||
        new Set(inst.slots.map(activity)).size > 1;
      const units: Unit[] = splitApart
        ? inst.slots.map((s, i) => ({ slots: [s], positions: [inst.positions[i]!] }))
        : [inst];

      for (const unit of units) {
        const first = unit.slots[0]!;
        if (!isComposableSlot(first)) {
          out.push({
            kind: "verbatim",
            dayIdx,
            position: unit.positions[0]!,
            slot: cloneSlotDraft(first),
          });
          continue;
        }
        // Split the active weeks wherever the per-member prescription
        // changes: a ramping slot (W1-2 @71, W3-4 @73, W5-6 @75) derives
        // into one block per equal-content week-set.
        const weekSets = new Map<string, number[]>();
        first.inactiveWeeks.forEach((off, w) => {
          if (off) return;
          const sig = JSON.stringify(unit.slots.map((s) => rowsAtWeek(s, w)));
          const arr = weekSets.get(sig);
          if (arr) arr.push(w);
          else weekSets.set(sig, [w]);
        });
        for (const weeks of weekSets.values()) {
          const members = unit.slots.map((s) => memberAtWeek(s, weeks[0]!));
          const placement: BlockPlacement = {
            dayIdx,
            slotIds: unit.slots.map((s) => s.id),
            positions: unit.positions,
            group: first.group
              ? { id: first.group.id, kind: first.group.kind }
              : null,
          };
          // weeks ascend by construction (the w-loop walks 0..N); the sort
          // keeps the key canonical even if that ever changes.
          const key = JSON.stringify([
            members.map(memberKey),
            [...weeks].sort((x, y) => x - y),
          ]);
          let list = buckets.get(key);
          if (!list) {
            list = [];
            buckets.set(key, list);
          }
          let target = list.find(
            (b) => !b.placements.some((p) => p.dayIdx === dayIdx),
          );
          if (!target) {
            target = { kind: "composed", members, weeks, placements: [] };
            list.push(target);
          }
          target.placements.push(placement);
        }
      }
    }
  });

  for (const list of buckets.values()) out.push(...list);

  // Stable presentation order: first day the block appears in, then its
  // first member's position there.
  const sortKey = (b: Block): [number, number] => {
    if (b.kind === "verbatim") return [b.dayIdx, b.position];
    let best: [number, number] = [Infinity, Infinity];
    for (const p of b.placements) {
      const pos = Math.min(...p.positions);
      if (p.dayIdx < best[0] || (p.dayIdx === best[0] && pos < best[1]))
        best = [p.dayIdx, pos];
    }
    return best;
  };
  return out
    .map((b) =>
      b.kind === "composed"
        ? { ...b, placements: [...b.placements].sort((a, c) => a.dayIdx - c.dayIdx) }
        : b,
    )
    .sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
};
